import Phaser from 'phaser';
import { COLORS, FONTS, GAME_HEIGHT, GAME_WIDTH, fontStr } from '@/config';
import {
  ASKETANK_FORSKUDD,
  CALIDRIS_VANITY_KOSTNAD,
  KAPELLE_FORSKUDD,
} from '@/systems/svenManus';
import type { SvenManusPubliseringsvei } from '@/data/types';
import { animateModalGlide } from '@/ui/modalGlide';
import { tooltipManager } from '@/ui/TooltipManager';

const SHEET_W = 880;

type Valg = {
  vei: SvenManusPubliseringsvei;
  ikon: string;
  tittel: string;
  undertittel: string;
  beskrivelse: string;
  farge: number;
  tooltip: string;
};

const VALG: ReadonlyArray<Valg> = [
  {
    vei: 'eget-forlag',
    ikon: '⌂',
    tittel: 'Eget forlag',
    undertittel: 'Du publiserer i ditt eget forlag (0 kr forskudd)',
    beskrivelse:
      'Etisk dilemma — bransje-Twitter kommer til å snakke om habilitet. -10 omdømme. +20% salgsboost, men kritikere blir biaserte.',
    farge: 0x862736,
    tooltip:
      'Eget forlag = -10 omdømme + -1 anmeldelses-bias.\nDu sparer forskudd, men kritikere kommer til å straffe deg for å publisere deg selv. Salget får +20% boost siden du eier markedsføringen, men reputasjonsskaden henger ved.',
  },
  {
    vei: 'kapelle',
    ikon: '📚',
    tittel: 'Kapelle Damm',
    undertittel: `Vurderes som vanlig debutant (${KAPELLE_FORSKUDD.toLocaleString('nb-NO')} kr ved aksept)`,
    beskrivelse:
      'Cirka 60% sjanse for kontrakt-tilbud. Boken går ut hos Kapelle, ikke i ditt forlag. Avslag gir -3 omdømme.',
    farge: 0x2c3e6b,
    tooltip:
      'Kapelle Damm: 60% sjanse for kontrakt hvis stats er ok.\nBoken utgis hos Kapelle, ikke i forlaget ditt — du tjener bare på forskuddet (ingen royalty). Avslag = -3 omdømme. Etablert vei for debutanter.',
  },
  {
    vei: 'asketank',
    ikon: '🎓',
    tittel: 'Asketank',
    undertittel: `Krever snitt 70+ (${ASKETANK_FORSKUDD.toLocaleString('nb-NO')} kr + 15 omdømme)`,
    beskrivelse:
      'Asketank er strengere — bare topp-tier blir antatt. Cirka 30% sjanse hvis stats er gode. Avslag gir -2 omdømme.',
    farge: 0x4a2c4f,
    tooltip:
      'Asketank: ~30% sjanse, krever snitt-stat 70+.\nAksept gir forskudd + 15 omdømme — en av de mest prestisjefulle veiene. Avslag = -2 omdømme. Bare for godt-trente forfattere.',
  },
  {
    vei: 'calidris',
    ikon: '!',
    tittel: 'Calidris (vanity)',
    undertittel: `Du betaler ${CALIDRIS_VANITY_KOSTNAD.toLocaleString('nb-NO')} kr for å bli utgitt`,
    beskrivelse:
      'Calidris publiserer MOT betaling. Dårlig distribusjon. -20 omdømme. Du blir åpen for flere Calidris-henvendelser.',
    farge: 0xd9a14b,
    tooltip:
      'Calidris vanity-publisering: du betaler kr.\n-20 omdømme. Dårlig distribusjon — boken blir nesten ikke solgt. Du blir også flagget som mål for flere Calidris-henvendelser de neste 30 dagene. Anbefales ikke.',
  },
];

export class PubliseringsveiModal {
  private layer?: Phaser.GameObjects.Container;
  private onVelg?: (vei: SvenManusPubliseringsvei) => void;

  constructor(private scene: Phaser.Scene) {}

  get isOpen(): boolean {
    return !!this.layer;
  }

  open(onVelg: (vei: SvenManusPubliseringsvei) => void): void {
    if (this.layer) return;
    this.onVelg = onVelg;
    this.layer = this.scene.add.container(0, 0).setDepth(190);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.78)
      .setOrigin(0, 0)
      .setInteractive();
    backdrop.on('pointerup', () => this.close());
    this.layer.add(backdrop);

    this.render();
    animateModalGlide(this.scene, this.layer);
  }

  close(): void {
    this.layer?.destroy(true);
    this.layer = undefined;
    this.onVelg = undefined;
  }

  private render(): void {
    if (!this.layer) return;
    const sheetX = (GAME_WIDTH - SHEET_W) / 2;
    const sheetY = 80;
    const sheetH = GAME_HEIGHT - 160;

    const sheet = this.scene.add
      .rectangle(sheetX, sheetY, SHEET_W, sheetH, COLORS.beige, 0.98)
      .setOrigin(0, 0)
      .setStrokeStyle(3, COLORS.wine, 0.7);
    this.layer.add(sheet);

    const topBorder = this.scene.add
      .rectangle(sheetX, sheetY, SHEET_W, 4, COLORS.wine, 1)
      .setOrigin(0, 0);
    this.layer.add(topBorder);

    const tittel = this.scene.add
      .text(sheetX + 28, sheetY + 28, 'Velg publiseringsvei', {
        fontFamily: FONTS.display,
        fontSize: fontStr(38),
        color: '#862736',
      })
      .setOrigin(0, 0);
    this.layer.add(tittel);

    const undertittel = this.scene.add
      .text(
        sheetX + 28,
        sheetY + 76,
        'Hvor skal manuset ditt ut? Hver vei har sine konsekvenser.',
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(16),
          color: '#5a4a3a',
          fontStyle: 'italic',
          wordWrap: { width: SHEET_W - 56 },
        },
      )
      .setOrigin(0, 0);
    this.layer.add(undertittel);

    const closeBtn = this.scene.add
      .text(sheetX + SHEET_W - 28, sheetY + 18, '×', {
        fontFamily: FONTS.display,
        fontSize: fontStr(64),
        color: '#1a1a1a',
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });
    closeBtn.on('pointerup', () => this.close());
    this.layer.add(closeBtn);

    const kortH = 250;
    const gap = 20;
    let kortY = sheetY + 180;
    for (const valg of VALG) {
      this.byggValgKort(sheetX + 28, kortY, SHEET_W - 56, kortH, valg);
      kortY += kortH + gap;
    }
  }

  private byggValgKort(
    x: number,
    y: number,
    w: number,
    h: number,
    valg: Valg,
  ): void {
    if (!this.layer) return;
    const bg = this.scene.add
      .rectangle(x, y, w, h, COLORS.white, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.oakDark, 0.4);
    this.layer.add(bg);

    const venstreBorder = this.scene.add
      .rectangle(x, y, 6, h, valg.farge, 1)
      .setOrigin(0, 0);
    this.layer.add(venstreBorder);

    const ikonBg = this.scene.add
      .circle(x + 50, y + 50, 28, valg.farge, 1)
      .setStrokeStyle(2, COLORS.wine, 0.4);
    this.layer.add(ikonBg);
    const ikon = this.scene.add
      .text(x + 50, y + 50, valg.ikon, {
        fontFamily: FONTS.display,
        fontSize: fontStr(30),
        color: '#ffffff',
        fontStyle: '700',
      })
      .setOrigin(0.5);
    this.layer.add(ikon);

    const tittel = this.scene.add
      .text(x + 112, y + 22, valg.tittel, {
        fontFamily: FONTS.display,
        fontSize: fontStr(24),
        color: '#1a1a1a',
      })
      .setOrigin(0, 0);
    this.layer.add(tittel);

    const undertittel = this.scene.add
      .text(x + 112, y + 72, valg.undertittel, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(14),
        color: '#862736',
        fontStyle: '600',
        wordWrap: { width: w - 112 - 24 },
      })
      .setOrigin(0, 0);
    this.layer.add(undertittel);

    const beskrivelse = this.scene.add
      .text(x + 112, y + 130, valg.beskrivelse, {
        fontFamily: FONTS.body,
        fontSize: fontStr(14),
        color: '#5a4a3a',
        wordWrap: { width: w - 112 - 24 },
      })
      .setOrigin(0, 0);
    this.layer.add(beskrivelse);

    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => bg.setFillStyle(COLORS.beige, 1));
    bg.on('pointerout', () => bg.setFillStyle(COLORS.white, 1));
    bg.on('pointerup', () => {
      const cb = this.onVelg;
      this.close();
      cb?.(valg.vei);
    });
    tooltipManager.attach(
      bg,
      () => valg.tooltip,
      () => ({ x: x + w / 2, y: y + 8 }),
    );
  }
}
